import { copyListToUser, deleteList, downloadList, editList, getLists } from "../../../../../api/ContactAPI";
import { nameColumn } from "../../../../../layouts/Table/columns/nameColumn";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Avatar,
  Flex,
  Group,
  Loader,
  NumberFormatter,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import MainTable from "../../../../../layouts/Table/MainTable";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import numberColumn from "../../../../../layouts/Table/columns/numberColumn";
import { sharingColumn } from "../../../../../layouts/Table/columns/sharingColumn";
import { tagsColumn } from "../../../../../layouts/Table/columns/tagsColumn";
import { useListingLoader } from "../../../../../components/hooks/ListingHooks";
import ListActions from "../../actions/ListActions";
import { IconChevronDown, IconPlus } from "@tabler/icons-react";
import SelectionMover from "../../../../../components/TableComponents/SelectionMover";
import SelectionSharer from "../../../../../components/TableComponents/SelectionSharer";
import SelectionDeleter from "../../../../../components/TableComponents/SelectionDeleter";
import MergeLists from "../../components/merge/MergeLists";
import FoldersDropdown from "./FoldersDropdown";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getListOrigin, getRowClasses } from "../listing";
import dateColumn from "../../../../../layouts/Table/columns/dateColumn";
import { formatThousand } from "../../../../../functions/utilFunctions";
import Jobs from "../../components/jobs/Jobs";
import ActionsCellChevron from "../../components/ActionsCellChevron";
import ListEnrichMultiple from "../../actions/ListEnrichMultiple";
import SelectionRemoveFolder from "../../../../../components/TableComponents/SelectionRemoveFolder";
import SelectionListDownload from "../../actions/SelectionListDownload";
import SelectionCopyToUser from "../../../../../components/View/SelectionCopyToUser";

const ContactListsListingFolder = () => {
  const lang = useLanguage((s) => s.language);
  const apiRef = useGridApiRef();
  const { contactListId, folderId } = useParams();
  const listId = useMemo(() => parseInt(contactListId), [contactListId]);

  const columns = useMemo(() => {
    return [
      numberColumn(),
      nameColumn({
        headerName: lang.prospects.list_name,
        disableLink: true,
        editFunction: () => editList,
        handleClick: () => {},
        width: 250,
        leftSection: function (row) {
          return (
            <Avatar
              color="initials"
              name={row.name}
              radius={"md"}
              size={36}
              variant="filled"
            />
          );
        },
        apiRef,
      }),
      {
        field: "jobs",
        headerName: lang.contact_lists.jobs.column_name,
        width: 160,
        filterable: false,
        renderCell: ({ row }) => {
          return (
            <Jobs row={row} apiRef={apiRef} isList setLoading={setLoading} />
          );
        },
      },
      {
        field: "number_of_contacts",
        headerName: lang.contact_lists.columns.number_of_contacts,
        type: "number",
        align: "left",
        headerAlign: "left",
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: ({ row, value }) => {
          return (
            <Tooltip
              label={`${formatThousand(
                row?.number_of_emails || 0,
              )} emails, ${formatThousand(
                row?.number_of_linkedin_url || 0,
              )} LinkedIn`}
            >
              <Group id={"loading-column" + row.id}>
                <NumberFormatter value={value} thousandSeparator=" " />

                {(row.state_details?.find((x) => x.state === "InProgress") ||
                  row.state_details?.find((x) => x.state === "InQueue")) && (
                  <Tooltip
                    label={
                      lang.contact_lists.jobs.in_progress
                        .loading_indicator_tooltip
                    }
                  >
                    <Loader color="blue" type="dots" size={"xs"} />
                  </Tooltip>
                )}
              </Group>
            </Tooltip>
          );
        },
      },
      dateColumn({ filterable: false }),
      tagsColumn({
        apiRef,
        editFunction: () => editList,
        filterable: false,
      }),
      sharingColumn({ apiRef, editFunction: () => editList }),
    ];
  }, []);

  const [loading, setLoading] = useState(folderId !== "none");
  const [rows, setRows] = useState([]);

  const toolbarActions = useCallback(
    ({ selectedIds, handleClose }) => {
      return (
        <>
          {/* <Button
            variant="light"
            color="primary"
            disabled={selectedIds.length === 0}
            leftSection={<IconDownload size={18} />}
            justify="start"
            className="to-blue"
          >
            {lang.contact_lists.download}
          </Button> */}
          <ListEnrichMultiple
            onFinish={() => {
              setLoading(true);
            }}
            selectedListIds={selectedIds}
            closeMenu={handleClose}
          />

          <SelectionSharer
            handleShare={editList}
            apiRef={apiRef}
            setTableLoading={setLoading}
            ids={selectedIds}
          />
          <MergeLists onFinish={setLoading} selectedIds={selectedIds} />
          <SelectionListDownload
            rows={rows}
            closeMenu={handleClose}
            handler={downloadList}
            selectedIds={selectedIds}
          />
          <SelectionCopyToUser
            closeMenu={handleClose}
            handler={copyListToUser}
            selectedIds={selectedIds}
          />
          <SelectionMover
            handleEdit={editList}
            apiRef={apiRef}
            setTableLoading={setLoading}
            ids={selectedIds}
            folderType={"ContactList"}
          />
          <SelectionRemoveFolder
            handleEdit={editList}
            apiRef={apiRef}
            setTableLoading={setLoading}
            ids={selectedIds}
            onFinish={() => setLoading(true)}
            rows={rows}
          />
          <SelectionDeleter
            handleDelete={deleteList}
            apiRef={apiRef}
            setTableLoading={setLoading}
            ids={selectedIds}
            setRows={setRows}
          />
        </>
      );
    },
    [apiRef, lang.contact_lists.download],
  );

  const actionsCell = useCallback(({ row }) => {
    return (
      <Group wrap="nowrap" gap={4} pos={"relative"}>
        {/* <QuickCampaign current={row} /> */}
        <ThemeIcon variant="subtle">{getListOrigin(row)}</ThemeIcon>

        <ListActions current={row} onFinish={() => setLoading(true)} />

        <ActionsCellChevron row={row} handleRowClick={handleRowClick} />
      </Group>
    );
  }, []);

  const navigate = useNavigate();
  const handleRowClick = ({ row }) => {
    navigate(`/contact-lists/folder/${folderId}/list/${row.id}`);
  };

  useEffect(() => {
    if (folderId !== "none") setLoading(true);
  }, [folderId]);

  useListingLoader({
    loading,
    setLoading,
    listGetter: getLists,
    responseField: "contact_lists",
    setRows,
    folderId: folderId,
  });

  return (
    <>
      <FoldersDropdown
        type="ContactList"
        getClickLink={(id) => `/contact-lists/folder/${id}/list/none`}
        getDeletedLink={() => `/contact-lists/folder/none/list/none`}
      />

      <Flex w={"100%"} pos={"relative"} flex={1}>
        <Group pos={"absolute"} bottom={72} right={0} style={{ zIndex: 16 }}>
          <Tooltip label={lang.prospects.add_list_to_folder}>
            <ActionIcon
              size={"xl"}
              variant="light"
              radius={"md"}
              component={Link}
              to={`/contact-lists/folder/${folderId}/list/${contactListId}/import`}
            >
              <IconPlus />
            </ActionIcon>
          </Tooltip>
        </Group>

        <MainTable
          apiRef={apiRef}
          columns={columns}
          rows={folderId === "none" ? [] : rows}
          loading={loading}
          setLoading={setLoading}
          id={"list-table"}
          actionsCell={actionsCell}
          hasFolder
          // pageName={"contact_lists"}
          // pageOrderName={"contact_list_order"}
          actionsCellWidth={112}
          disableGotoPage
          defaultPerPage={15}
          // disablePerPage
          onRowClick={handleRowClick}
          toolbarActions={toolbarActions}
          noRowsDisplay={
            folderId ? null : (
              <Text ta={"center"} size="lg">
                {lang.prospects.choose_folder}
              </Text>
            )
          }
          getRowClassName={(params) =>
            getRowClasses({ params, mainId: contactListId, integerId: listId })
          }
          tableSx={{
            "& .table-toolbar": {
              minWidth: 420,
            },
            "& .MuiDataGrid-pinnedColumnHeaders": {
              border: "none !important",
              background: "none !important",
            },
            "& .MuiDataGrid-pinnedColumns": {
              border: "none !important",
              background: "none !important",
            },
            "& .MuiDataGrid-cell": {
              border: "none !important",
              borderRadius: 12,
            },
            "& .MuiDataGrid-columnHeaders": {
              border: "none !important",
            },
            "& .MuiDataGrid-main": {
              border: "none !important",
              borderRadius: 4,
              background: "none !important",
            },
            "& .table-row": {
              cursor: "pointer",
              borderRadius: 3,
              marginBottom: 1,
              background: "#fff !important",
              border: "1px solid var(--mantine-color-primary-1)",
            },
            "& .MuiDataGrid-pinnedColumns--right .table-row": {
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              borderLeft: "none",
            },
            "& .MuiDataGrid-pinnedColumns--left .table-row": {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              borderRight: "none",
            },
            "& .MuiDataGrid-virtualScrollerRenderZone .table-row": {
              borderRadius: 0,
            },
            "& .MuiDataGrid-selectedRowCount": {
              visibility: "hidden",
            },
            "& .MuiPagination-root": {
              left: 0,
              transform: "none",
            },
            "& .Mui-selected": {
              color: "",
            },
            "& .MuiDataGrid-footerContainer": {
              border: 0,
            },
            "& .table-search": {
              width: "180px !important",
            },
          }}
        />
      </Flex>
    </>
  );
};

export default ContactListsListingFolder;
